import { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FileText,
  Loader2,
  AlertCircle,
  RefreshCw,
  Download,
} from "lucide-react";
import {
  getAllPaidInvoices,
  downloadCommissionInvoice,
} from "../../store/slices/adminReportSlice";
import { showApiError, showApiSuccess } from "../../utils/toast";
import useUrlFilters from "../../hooks/useUrlFilters";
import AdminFilterBar from "../../components/ui/AdminFilterBar";
import Pagination from "../../components/tables/Pagination";

// ─── constants ───────────────────────────────────────────────────────────────

const now = new Date();
const currentYear = now.getFullYear();
const currentMonth = now.getMonth() + 1;
const defaultMonth = currentMonth === 1 ? 12 : currentMonth - 1;
const defaultYear = currentMonth === 1 ? currentYear - 1 : currentYear;

const DEFAULT_FILTERS = {
  month: defaultMonth,
  year: defaultYear,
};

// ─── helpers ─────────────────────────────────────────────────────────────────

const fmt = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount || 0);

const fmtDate = (dateString) => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdminInvoices() {
  const dispatch = useDispatch();

  const {
    invoices,
    invoicesPagination,
    invoicesPeriod,
    invoicesLoading,
    invoicesError,
    invoiceLoadingMap,
  } = useSelector((state) => state.adminReport);

  // ── filters (synced with URL) ──────────────────────────────────────────────
  const { filters, updateFilter, resetFilters, currentPage, setPage } = useUrlFilters(
    DEFAULT_FILTERS,
    { numberFields: ["month", "year"] }
  );

  // ── fetch invoices ─────────────────────────────────────────────────────────
  const fetchInvoices = useCallback(() => {
    dispatch(getAllPaidInvoices({ ...filters, per_page: 15, page: currentPage }));
  }, [dispatch, filters, currentPage]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // ── filter actions ─────────────────────────────────────────────────────────
  const handlePageChange = (page) => {
    setPage(page);
  };

  // ── download invoice ───────────────────────────────────────────────────────
  const handleDownloadInvoice = useCallback(async (businessUuid) => {
    const result = await dispatch(downloadCommissionInvoice({
      businessUuid,
      month: filters.month,
      year: filters.year,
    }));
    if (downloadCommissionInvoice.fulfilled.match(result)) {
      showApiSuccess("Invoice downloaded successfully");
    } else {
      showApiError(null, result.payload || "Invoice not available");
    }
  }, [dispatch, filters.month, filters.year]);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Page header */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center space-x-3">
            <div className="bg-purple-100 p-2 rounded-lg">
              <FileText className="h-6 w-6 sm:h-7 sm:w-7 text-purple-600" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">All Invoices</h1>
              <p className="text-xs sm:text-sm text-gray-500">View and download paid commission invoices across all businesses</p>
            </div>
          </div>
          <button
            onClick={fetchInvoices}
            disabled={invoicesLoading}
            className="self-end sm:self-auto p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-40"
            title="Refresh"
          >
            <RefreshCw className={`h-5 w-5 ${invoicesLoading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Filters + Table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {/* Filters */}
        <div className="p-4 border-b border-gray-200">
          <AdminFilterBar
            filters={filters}
            onFilterChange={updateFilter}
            onReset={resetFilters}
            dropdowns={{}}
            showFields={["year", "month"]}
          />
        </div>

        {/* Table header info */}
        <div className="px-4 sm:px-6 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
          <h2 className="text-sm font-semibold text-gray-700">
            Paid Invoices
            {invoicesPeriod && (
              <span className="ml-1 font-normal text-gray-500">
                — {invoicesPeriod.month_name} {invoicesPeriod.year}
              </span>
            )}
          </h2>
          {invoicesPagination && (
            <span className="text-xs text-gray-500">{invoicesPagination.total} invoices</span>
          )}
        </div>

        {/* Table content */}
        {invoicesLoading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-gray-500">
            <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
            <span>Loading invoices…</span>
          </div>
        ) : invoicesError ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <AlertCircle className="h-10 w-10 text-red-400" />
            <p className="text-gray-600">Failed to load invoices.</p>
            <button onClick={fetchInvoices} className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors">
              Try Again
            </button>
          </div>
        ) : invoices.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <FileText className="h-12 w-12 text-gray-300" />
            <p className="text-gray-500 font-medium">No paid invoices for this period</p>
            <p className="text-gray-400 text-sm">Try a different month or year.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["#", "Business Name", "Transactions", "Total Revenue", "Commission", "Paid At", "Action"].map((col) => (
                    <th
                      key={col}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {invoices.map((row, idx) => (
                  <tr key={row.uuid} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-400 font-mono text-xs">
                      {(invoicesPagination
                        ? (invoicesPagination.current_page - 1) * invoicesPagination.per_page
                        : 0) + idx + 1}
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-gray-900 whitespace-nowrap">{row.business_name}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      {row.transaction_count}
                    </td>
                    <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">
                      {fmt(row.total_revenue)}
                    </td>
                    <td className="px-4 py-3 font-bold text-orange-600 whitespace-nowrap">
                      {fmt(row.commission)}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {fmtDate(row.paid_at)}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleDownloadInvoice(row.business_uuid)}
                        disabled={invoiceLoadingMap[row.business_uuid]}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 transition-colors disabled:opacity-50"
                      >
                        {invoiceLoadingMap[row.business_uuid] ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Download className="h-3.5 w-3.5" />
                        )}
                        {invoiceLoadingMap[row.business_uuid] ? "Downloading…" : "Download"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {invoicesPagination && invoicesPagination.last_page > 1 && (
          <Pagination
            currentPage={invoicesPagination.current_page}
            totalPages={invoicesPagination.last_page}
            totalItems={invoicesPagination.total}
            itemsPerPage={invoicesPagination.per_page}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </div>
  );
}
